import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiBriefcase,
    FiCheck,
    FiChevronDown,
    FiMapPin,
    FiPlus,
    FiSearch,
    FiUser,
    FiX,
} from "react-icons/fi";

import API from "../../../../../../../utils/apiCalling";
import { config } from "../../../../../../../utils/apiUrl";

import "./CustomerSection.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const EMPTY_CUSTOMER = {
    customerId: "",
    customerMode: "MANUAL",

    customerName: "",
    mobile: "",
    alternateMobile: "",
    email: "",
    companyName: "",

    customerType: "CONSUMER",
    taxTreatment: "CONSUMER",

    gstin: "",
    pan: "",

    billingAddress: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        stateCode: "",
        pincode: "",
        country: "India",
    },

    shippingAddress: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        stateCode: "",
        pincode: "",
        country: "India",
    },

    sameAsBillingAddress: true,
};

const CUSTOMER_TYPES = [
    {
        value: "CONSUMER",
        label: "Consumer",
    },
    {
        value: "BUSINESS",
        label: "Business",
    },
    {
        value: "DEALER",
        label: "Dealer",
    },
    {
        value: "RESELLER",
        label: "Reseller",
    },
    {
        value: "GOVERNMENT",
        label: "Government",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];

const TAX_TREATMENTS = [
    {
        value: "CONSUMER",
        label: "Consumer",
    },
    {
        value: "REGISTERED",
        label: "GST Registered",
    },
    {
        value: "UNREGISTERED",
        label: "GST Unregistered",
    },
    {
        value: "COMPOSITION",
        label: "Composition",
    },
    {
        value: "SEZ",
        label: "SEZ",
    },
    {
        value: "OVERSEAS",
        label: "Overseas",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const api =
    new API();

const normalizeText = (
    value
) => {
    return String(
        value ?? ""
    ).trim();
};

const normalizeMobile = (
    value
) => {
    return String(
        value ?? ""
    ).replace(
        /\D/g,
        ""
    ).slice(
        0,
        10
    );
};

const normalizePincode = (
    value
) => {
    return String(
        value ?? ""
    ).replace(
        /\D/g,
        ""
    ).slice(
        0,
        6
    );
};

const getCustomerId = (
    customer
) => {
    return (
        customer?._id ||
        customer?.id ||
        ""
    );
};

const getCustomerName = (
    customer
) => {
    return (
        customer?.customerName ||
        customer?.name ||
        customer?.fullName ||
        customer?.companyName ||
        ""
    );
};

const getCustomerMobile = (
    customer
) => {
    return (
        customer?.mobile ||
        customer?.phone ||
        customer?.mobileNumber ||
        ""
    );
};

const getResponseCustomers = (
    response
) => {
    const candidates = [
        response?.data?.customers,
        response?.data?.data?.customers,
        response?.customers,
        response?.result?.customers,
        response?.data?.records,
        response?.records,
    ];

    return (
        candidates.find(
            Array.isArray
        ) ||
        []
    );
};

const getResponseError = (
    error
) => {
    return (
        error?.response?.data?.message ||
        error?.message ||
        "Unable to search customers."
    );
};

const normalizeAddress = (
    address,
    fallback = {}
) => {
    if (
        typeof address ===
        "string"
    ) {
        return {
            addressLine1:
                normalizeText(
                    address
                ),

            addressLine2:
                "",

            city:
                normalizeText(
                    fallback?.city
                ),

            state:
                normalizeText(
                    fallback?.state
                ),

            stateCode:
                normalizeText(
                    fallback?.stateCode
                ),

            pincode:
                normalizeText(
                    fallback?.pincode
                ),

            country:
                normalizeText(
                    fallback?.country ||
                    "India"
                ),
        };
    }

    const source =
        address &&
        typeof address ===
            "object"
            ? address
            : {};

    return {
        addressLine1:
            normalizeText(
                source.addressLine1 ||
                source.address ||
                fallback.addressLine1 ||
                fallback.address
            ),

        addressLine2:
            normalizeText(
                source.addressLine2 ||
                fallback.addressLine2
            ),

        city:
            normalizeText(
                source.city ||
                fallback.city
            ),

        state:
            normalizeText(
                source.state ||
                fallback.state
            ),

        stateCode:
            normalizeText(
                source.stateCode ||
                fallback.stateCode
            ),

        pincode:
            normalizeText(
                source.pincode ||
                source.pinCode ||
                fallback.pincode ||
                fallback.pinCode
            ),

        country:
            normalizeText(
                source.country ||
                fallback.country ||
                "India"
            ),
    };
};

const normalizeCustomerValue = (
    value
) => {
    const source =
        value &&
        typeof value ===
            "object"
            ? value
            : {};

    return {
        ...EMPTY_CUSTOMER,
        ...source,

        customerId:
            source.customerId ||
            source.customer ||
            "",

        customerMode:
            source.customerMode ||
            (
                source.customerId ||
                source.customer
                    ? "SAVED"
                    : "MANUAL"
            ),

        customerName:
            source.customerName ||
            source.name ||
            "",

        mobile:
            source.mobile ||
            source.customerMobile ||
            "",

        alternateMobile:
            source.alternateMobile ||
            "",

        email:
            source.email ||
            source.customerEmail ||
            "",

        companyName:
            source.companyName ||
            "",

        customerType:
            source.customerType ||
            "CONSUMER",

        taxTreatment:
            source.taxTreatment ||
            "CONSUMER",

        gstin:
            source.gstin ||
            "",

        pan:
            source.pan ||
            "",

        billingAddress:
            normalizeAddress(
                source.billingAddress ||
                source.billingAddressSnapshot ||
                source.address,
                source
            ),

        shippingAddress:
            normalizeAddress(
                source.shippingAddress ||
                source.shippingAddressSnapshot,
                source.billingAddress ||
                source.billingAddressSnapshot ||
                source
            ),

        sameAsBillingAddress:
            source.sameAsBillingAddress !==
            false,
    };
};

const mapSavedCustomer = (
    customer
) => {
    const billingAddress =
        normalizeAddress(
            customer?.billingAddress ||
            customer?.address,
            customer
        );

    const shippingAddress =
        normalizeAddress(
            customer?.shippingAddress,
            billingAddress
        );

    return {
        customerId:
            getCustomerId(
                customer
            ),

        customerMode:
            "SAVED",

        customerName:
            getCustomerName(
                customer
            ),

        mobile:
            getCustomerMobile(
                customer
            ),

        alternateMobile:
            customer?.alternateMobile ||
            "",

        email:
            customer?.email ||
            "",

        companyName:
            customer?.companyName ||
            "",

        customerType:
            customer?.customerType ||
            "CONSUMER",

        taxTreatment:
            customer?.taxTreatment ||
            (
                customer?.gstin
                    ? "REGISTERED"
                    : "CONSUMER"
            ),

        gstin:
            customer?.gstin ||
            "",

        pan:
            customer?.pan ||
            "",

        billingAddress,

        shippingAddress,

        sameAsBillingAddress:
            customer
                ?.sameAsBillingAddress !==
            false,
    };
};

const validateCustomer = (
    customer
) => {
    const errors = {};

    if (
        !normalizeText(
            customer.customerName
        )
    ) {
        errors.customerName =
            "Customer name is required.";
    }

    const mobile =
        normalizeMobile(
            customer.mobile
        );

    if (
        mobile &&
        !/^[6-9]\d{9}$/.test(
            mobile
        )
    ) {
        errors.mobile =
            "Enter a valid 10-digit mobile number.";
    }

    const email =
        normalizeText(
            customer.email
        );

    if (
        email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
            email
        )
    ) {
        errors.email =
            "Enter a valid email address.";
    }

    const gstin =
        normalizeText(
            customer.gstin
        ).toUpperCase();

    if (
        gstin &&
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
            gstin
        )
    ) {
        errors.gstin =
            "Enter a valid GSTIN.";
    }

    const pan =
        normalizeText(
            customer.pan
        ).toUpperCase();

    if (
        pan &&
        !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(
            pan
        )
    ) {
        errors.pan =
            "Enter a valid PAN.";
    }

    const pincode =
        normalizePincode(
            customer
                ?.billingAddress
                ?.pincode
        );

    if (
        pincode &&
        pincode.length !==
            6
    ) {
        errors.billingPincode =
            "Pincode must be 6 digits.";
    }

    return errors;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CustomerSection = ({
    value,
    onChange,
    onValidationChange,
    disabled = false,
    required = true,
    allowManual = true,
    showShippingAddress = true,
    searchLimit = 15,
}) => {
    const customer =
        useMemo(
            () =>
                normalizeCustomerValue(
                    value
                ),
            [value]
        );

    const rootRef =
        useRef(null);

    const searchRequestRef =
        useRef(0);

    const [
        searchText,
        setSearchText,
    ] = useState(
        customer.customerMode ===
            "SAVED"
            ? customer.customerName
            : ""
    );

    const [
        searchResults,
        setSearchResults,
    ] = useState([]);

    const [
        searchLoading,
        setSearchLoading,
    ] = useState(false);

    const [
        searchError,
        setSearchError,
    ] = useState("");

    const [
        dropdownOpen,
        setDropdownOpen,
    ] = useState(false);

    const [
        activeResultIndex,
        setActiveResultIndex,
    ] = useState(-1);

    const [
        detailsOpen,
        setDetailsOpen,
    ] = useState(true);

    const [
        moreDetailsOpen,
        setMoreDetailsOpen,
    ] = useState(false);

    const [
        gstOpen,
        setGstOpen,
    ] = useState(false);

    const [
        shippingOpen,
        setShippingOpen,
    ] = useState(false);

    const [
        touched,
        setTouched,
    ] = useState({});

    const errors =
        useMemo(
            () =>
                validateCustomer(
                    customer
                ),
            [customer]
        );

    const emitChange =
        useCallback(
            (
                patch
            ) => {
                if (
                    typeof onChange !==
                    "function"
                ) {
                    return;
                }

                const next = {
                    ...customer,
                    ...patch,
                };

                if (
                    patch.billingAddress
                ) {
                    next.billingAddress = {
                        ...customer.billingAddress,
                        ...patch.billingAddress,
                    };
                }

                if (
                    patch.shippingAddress
                ) {
                    next.shippingAddress = {
                        ...customer.shippingAddress,
                        ...patch.shippingAddress,
                    };
                }

                if (
                    next.sameAsBillingAddress
                ) {
                    next.shippingAddress = {
                        ...next.billingAddress,
                    };
                }

                onChange(next);
            },
            [
                customer,
                onChange,
            ]
        );

    useEffect(() => {
        if (
            typeof onValidationChange ===
            "function"
        ) {
            onValidationChange({
                valid:
                    Object.keys(
                        errors
                    ).length ===
                    0,

                errors,
            });
        }
    }, [
        errors,
        onValidationChange,
    ]);

    useEffect(() => {
        if (
            customer.customerMode ===
            "SAVED"
        ) {
            setSearchText(
                customer.customerName
            );
        }
    }, [
        customer.customerId,
        customer.customerMode,
        customer.customerName,
    ]);

    useEffect(() => {
        const handleOutsideClick = (
            event
        ) => {
            if (
                rootRef.current &&
                !rootRef.current.contains(
                    event.target
                )
            ) {
                setDropdownOpen(
                    false
                );

                setActiveResultIndex(
                    -1
                );
            }
        };

        document.addEventListener(
            "mousedown",
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );
        };
    }, []);

    useEffect(() => {
        const keyword =
            normalizeText(
                searchText
            );

        if (
            customer.customerMode ===
            "SAVED" &&
            customer.customerId &&
            keyword ===
                customer.customerName
        ) {
            setSearchResults([]);
            setSearchError("");
            return undefined;
        }

        if (
            keyword.length <
            2
        ) {
            setSearchResults([]);
            setSearchLoading(false);
            setSearchError("");
            return undefined;
        }

        const timer =
            window.setTimeout(
                async () => {
                    const requestId =
                        searchRequestRef
                            .current +
                        1;

                    searchRequestRef.current =
                        requestId;

                    try {
                        setSearchLoading(
                            true
                        );

                        setSearchError(
                            ""
                        );

                        /*
                        |--------------------------------------------------------------------------
                        | BUILD QUERY STRING EXPLICITLY
                        |--------------------------------------------------------------------------
                        |
                        | The runtime request was reaching:
                        |
                        | /v1/admin/billing/customers/search
                        |
                        | without ?search=...
                        |
                        | Building the query here makes this work even if the
                        | currently running API helper does not serialize GET params.
                        |
                        |--------------------------------------------------------------------------
                        */

                        const queryParams =
                            new URLSearchParams({
                                search:
                                    keyword,

                                page:
                                    "1",

                                limit:
                                    String(
                                        searchLimit
                                    ),

                                includeBilled:
                                    "true",
                            });


                        const searchUrl =
                            `${config.searchBillingCustomers}?${queryParams.toString()}`;


                        const response =
                            await api.get(
                                searchUrl
                            );

                        if (
                            requestId !==
                            searchRequestRef
                                .current
                        ) {
                            return;
                        }

                        const customers =
                            getResponseCustomers(
                                response
                            );

                        setSearchResults(
                            customers
                        );

                        setDropdownOpen(
                            true
                        );

                        setActiveResultIndex(
                            customers.length
                                ? 0
                                : -1
                        );
                    } catch (error) {
                        if (
                            requestId !==
                            searchRequestRef
                                .current
                        ) {
                            return;
                        }

                        setSearchResults(
                            []
                        );

                        setSearchError(
                            getResponseError(
                                error
                            )
                        );

                        setDropdownOpen(
                            true
                        );
                    } finally {
                        if (
                            requestId ===
                            searchRequestRef
                                .current
                        ) {
                            setSearchLoading(
                                false
                            );
                        }
                    }
                },
                350
            );

        return () => {
            window.clearTimeout(
                timer
            );
        };
    }, [
        customer.customerId,
        customer.customerMode,
        customer.customerName,
        searchLimit,
        searchText,
    ]);

    const handleFieldChange =
        useCallback(
            (
                field,
                nextValue
            ) => {
                emitChange({
                    [field]:
                        nextValue,
                });
            },
            [emitChange]
        );

    const handleAddressChange =
        useCallback(
            (
                addressType,
                field,
                nextValue
            ) => {
                emitChange({
                    [addressType]: {
                        [field]:
                            nextValue,
                    },
                });
            },
            [emitChange]
        );

    const handleSelectCustomer =
        useCallback(
            (
                selectedCustomer
            ) => {
                const mapped =
                    mapSavedCustomer(
                        selectedCustomer
                    );

                onChange?.(
                    mapped
                );

                setSearchText(
                    mapped.customerName
                );

                setSearchResults(
                    []
                );

                setDropdownOpen(
                    false
                );

                setActiveResultIndex(
                    -1
                );

                setTouched({});

                setGstOpen(
                    Boolean(
                        mapped.gstin ||
                        mapped.pan ||
                        mapped.taxTreatment ===
                            "REGISTERED"
                    )
                );

                setShippingOpen(
                    mapped.sameAsBillingAddress ===
                        false
                );
            },
            [onChange]
        );

    const handleUseManualCustomer =
        useCallback(() => {
            const manualName =
                normalizeText(
                    searchText
                );

            onChange?.({
                ...EMPTY_CUSTOMER,

                customerMode:
                    "MANUAL",

                customerName:
                    manualName,
            });

            setSearchResults(
                []
            );

            setDropdownOpen(
                false
            );

            setActiveResultIndex(
                -1
            );

            setTouched({});
            setDetailsOpen(true);
        }, [
            onChange,
            searchText,
        ]);

    const handleClearSavedCustomer =
        useCallback(() => {
            onChange?.({
                ...EMPTY_CUSTOMER,
            });

            setSearchText("");
            setSearchResults([]);
            setDropdownOpen(false);
            setActiveResultIndex(-1);
            setTouched({});
            setDetailsOpen(true);
            setGstOpen(false);
            setShippingOpen(false);
        }, [onChange]);

    const handleSearchKeyDown =
        useCallback(
            (
                event
            ) => {
                if (
                    !dropdownOpen
                ) {
                    if (
                        event.key ===
                        "ArrowDown"
                    ) {
                        setDropdownOpen(
                            true
                        );
                    }

                    return;
                }

                if (
                    event.key ===
                    "ArrowDown"
                ) {
                    event.preventDefault();

                    setActiveResultIndex(
                        (
                            previous
                        ) =>
                            Math.min(
                                previous +
                                    1,
                                searchResults.length -
                                    1
                            )
                    );

                    return;
                }

                if (
                    event.key ===
                    "ArrowUp"
                ) {
                    event.preventDefault();

                    setActiveResultIndex(
                        (
                            previous
                        ) =>
                            Math.max(
                                previous -
                                    1,
                                0
                            )
                    );

                    return;
                }

                if (
                    event.key ===
                    "Enter"
                ) {
                    event.preventDefault();

                    if (
                        activeResultIndex >=
                            0 &&
                        searchResults[
                            activeResultIndex
                        ]
                    ) {
                        handleSelectCustomer(
                            searchResults[
                                activeResultIndex
                            ]
                        );

                        return;
                    }

                    if (
                        allowManual &&
                        normalizeText(
                            searchText
                        )
                    ) {
                        handleUseManualCustomer();
                    }

                    return;
                }

                if (
                    event.key ===
                    "Escape"
                ) {
                    setDropdownOpen(
                        false
                    );

                    setActiveResultIndex(
                        -1
                    );
                }
            },
            [
                activeResultIndex,
                allowManual,
                dropdownOpen,
                handleSelectCustomer,
                handleUseManualCustomer,
                searchResults,
                searchText,
            ]
        );

    const markTouched =
        useCallback(
            (
                field
            ) => {
                setTouched(
                    (
                        previous
                    ) => ({
                        ...previous,
                        [field]:
                            true,
                    })
                );
            },
            []
        );

    const getVisibleError =
        useCallback(
            (
                field
            ) => {
                return touched[field]
                    ? errors[field]
                    : "";
            },
            [
                errors,
                touched,
            ]
        );

    return (
        <section
            ref={rootRef}
            className={[
                "billing-customer-section",
                moreDetailsOpen ? "billing-customer-show-more" : "",
                shippingOpen ? "billing-customer-show-addresses" : "",
                gstOpen ? "billing-customer-show-gst" : "",
            ].filter(Boolean).join(" ")}
        >
            <div className="billing-customer-section-header">
                <div className="billing-customer-section-title">
                    <span className="billing-customer-section-icon">
                        <FiUser />
                    </span>

                    <div>
                        <h2>
                            Customer Details
                        </h2>

                        <p>
                            Select an existing customer or continue with manual customer details.
                        </p>
                    </div>
                </div>

                {customer.customerMode ===
                    "SAVED" &&
                    customer.customerId && (
                        <span className="billing-customer-saved-badge">
                            <FiCheck />

                            Saved Customer
                        </span>
                    )}
            </div>

            <div className="billing-customer-search-block">
                <label htmlFor="billing-customer-search">
                    Search Customer

                    {required && (
                        <span>
                            *
                        </span>
                    )}
                </label>

                <div className="billing-customer-search-control">
                    <FiSearch />

                    <input
                        id="billing-customer-search"
                        type="text"
                        value={searchText}
                        placeholder="Search by customer name, mobile, company or GSTIN"
                        autoComplete="off"
                        disabled={
                            disabled
                        }
                        onFocus={() => {
                            if (
                                normalizeText(
                                    searchText
                                ).length >=
                                2
                            ) {
                                setDropdownOpen(
                                    true
                                );
                            }
                        }}
                        onChange={(
                            event
                        ) => {
                            const nextValue =
                                event.target
                                    .value;

                            setSearchText(
                                nextValue
                            );

                            if (
                                customer.customerMode ===
                                    "SAVED" &&
                                customer.customerId
                            ) {
                                onChange?.({
                                    ...EMPTY_CUSTOMER,

                                    customerMode:
                                        "MANUAL",

                                    customerName:
                                        nextValue,
                                });
                            }
                        }}
                        onKeyDown={
                            handleSearchKeyDown
                        }
                    />

                    {searchLoading && (
                        <span className="billing-customer-search-spinner" />
                    )}

                    {!searchLoading &&
                        searchText && (
                            <button
                                type="button"
                                className="billing-customer-search-clear"
                                aria-label="Clear customer search"
                                disabled={
                                    disabled
                                }
                                onClick={
                                    handleClearSavedCustomer
                                }
                            >
                                <FiX />
                            </button>
                        )}
                </div>

                {dropdownOpen && (
                    <div className="billing-customer-search-dropdown">
                        {searchLoading && (
                            <div className="billing-customer-search-state">
                                Searching customers...
                            </div>
                        )}

                        {!searchLoading &&
                            searchError && (
                                <div className="billing-customer-search-state billing-customer-search-error">
                                    <FiAlertCircle />

                                    {searchError}
                                </div>
                            )}

                        {!searchLoading &&
                            !searchError &&
                            searchResults.map(
                                (
                                    result,
                                    index
                                ) => {
                                    const resultId =
                                        getCustomerId(
                                            result
                                        );

                                    const resultName =
                                        getCustomerName(
                                            result
                                        );

                                    const resultMobile =
                                        getCustomerMobile(
                                            result
                                        );

                                    return (
                                        <button
                                            key={
                                                resultId ||
                                                `${resultName}-${index}`
                                            }
                                            type="button"
                                            className={
                                                index ===
                                                activeResultIndex
                                                    ? "billing-customer-result billing-customer-result-active"
                                                    : "billing-customer-result"
                                            }
                                            onMouseEnter={() =>
                                                setActiveResultIndex(
                                                    index
                                                )
                                            }
                                            onClick={() =>
                                                handleSelectCustomer(
                                                    result
                                                )
                                            }
                                        >
                                            <span className="billing-customer-result-avatar">
                                                {String(
                                                    resultName ||
                                                    "C"
                                                )
                                                    .charAt(
                                                        0
                                                    )
                                                    .toUpperCase()}
                                            </span>

                                            <span className="billing-customer-result-content">
                                                <strong>
                                                    {resultName ||
                                                        "Unnamed Customer"}
                                                </strong>

                                                <small>
                                                    {[
                                                        resultMobile,
                                                        result
                                                            ?.companyName,
                                                        result
                                                            ?.gstin,
                                                    ]
                                                        .filter(
                                                            Boolean
                                                        )
                                                        .join(
                                                            " • "
                                                        ) ||
                                                        "No additional details"}
                                                </small>
                                            </span>

                                            <FiChevronDown className="billing-customer-result-arrow" />
                                        </button>
                                    );
                                }
                            )}

                        {!searchLoading &&
                            !searchError &&
                            searchResults.length ===
                                0 &&
                            normalizeText(
                                searchText
                            ).length >=
                                2 && (
                                <div className="billing-customer-search-state">
                                    No saved customer found.
                                </div>
                            )}

                        {allowManual &&
                            normalizeText(
                                searchText
                            ) && (
                                <button
                                    type="button"
                                    className="billing-customer-manual-option"
                                    onClick={
                                        handleUseManualCustomer
                                    }
                                >
                                    <FiPlus />

                                    <span>
                                        Continue as manual customer

                                        <small>
                                            Use “{normalizeText(
                                                searchText
                                            )}” without creating a customer record.
                                        </small>
                                    </span>
                                </button>
                            )}
                    </div>
                )}
            </div>

            {customer.customerMode ===
                "SAVED" &&
                customer.customerId && (
                    <div className="billing-customer-selected-card">
                        <div className="billing-customer-selected-main">
                            <span className="billing-customer-selected-avatar">
                                {String(
                                    customer.customerName ||
                                    "C"
                                )
                                    .charAt(0)
                                    .toUpperCase()}
                            </span>

                            <div>
                                <strong>
                                    {customer.customerName}
                                </strong>

                                <span>
                                    {[
                                        customer.mobile,
                                        customer.companyName,
                                        customer.gstin,
                                    ]
                                        .filter(
                                            Boolean
                                        )
                                        .join(
                                            " • "
                                        ) ||
                                        "Saved customer"}
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            disabled={
                                disabled
                            }
                            onClick={
                                handleClearSavedCustomer
                            }
                        >
                            Change Customer
                        </button>
                    </div>
                )}

            <div className="billing-customer-toolbar" role="tablist" aria-label="Customer details sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={detailsOpen && !moreDetailsOpen && !gstOpen && !shippingOpen}
                    className={
                        detailsOpen && !moreDetailsOpen && !gstOpen && !shippingOpen
                            ? "billing-customer-toolbar-button billing-customer-toolbar-button-active"
                            : "billing-customer-toolbar-button"
                    }
                    onClick={() => {
                        setDetailsOpen(true);
                        setMoreDetailsOpen(false);
                        setGstOpen(false);
                        setShippingOpen(false);
                    }}
                >
                    <FiUser />

                    Basic Details
                </button>

                <button
                    type="button"
                    role="tab"
                    aria-selected={moreDetailsOpen}
                    className={
                        moreDetailsOpen
                            ? "billing-customer-toolbar-button billing-customer-toolbar-button-active"
                            : "billing-customer-toolbar-button"
                    }
                    onClick={() => {
                        setDetailsOpen(true);
                        setMoreDetailsOpen(true);
                        setGstOpen(false);
                        setShippingOpen(false);
                    }}
                >
                    <FiPlus />
                    More Details
                </button>

                <button
                    type="button"
                    role="tab"
                    aria-selected={gstOpen}
                    className={
                        gstOpen
                            ? "billing-customer-toolbar-button billing-customer-toolbar-button-active"
                            : "billing-customer-toolbar-button"
                    }
                    onClick={() => {
                        setDetailsOpen(false);
                        setMoreDetailsOpen(false);
                        setGstOpen(true);
                        setShippingOpen(false);
                    }}
                >
                    <FiBriefcase />

                    GST Details
                </button>

                {showShippingAddress && (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={shippingOpen}
                        className={
                            shippingOpen
                                ? "billing-customer-toolbar-button billing-customer-toolbar-button-active"
                                : "billing-customer-toolbar-button"
                        }
                        onClick={() => {
                            setDetailsOpen(true);
                            setMoreDetailsOpen(false);
                            setGstOpen(false);
                            setShippingOpen(true);
                        }}
                    >
                        <FiMapPin />

                        Shipping Address
                    </button>
                )}
            </div>

            {detailsOpen && (
                <div className="billing-customer-panel billing-customer-basic-panel">
                    <div className="billing-customer-grid billing-customer-grid-three billing-customer-basic-grid">
                        <div className="billing-customer-field">
                            <label>
                                Customer Name

                                {required && (
                                    <span>
                                        *
                                    </span>
                                )}
                            </label>

                            <input
                                type="text"
                                value={
                                    customer.customerName
                                }
                                maxLength={
                                    100
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Enter customer name"
                                onBlur={() =>
                                    markTouched(
                                        "customerName"
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "customerName",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            {getVisibleError(
                                "customerName"
                            ) && (
                                <small className="billing-customer-field-error">
                                    {getVisibleError(
                                        "customerName"
                                    )}
                                </small>
                            )}
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                Mobile Number
                            </label>

                            <input
                                type="tel"
                                inputMode="numeric"
                                value={
                                    customer.mobile
                                }
                                maxLength={
                                    10
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="10-digit mobile number"
                                onBlur={() =>
                                    markTouched(
                                        "mobile"
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "mobile",
                                        normalizeMobile(
                                            event.target
                                                .value
                                        )
                                    )
                                }
                            />

                            {getVisibleError(
                                "mobile"
                            ) && (
                                <small className="billing-customer-field-error">
                                    {getVisibleError(
                                        "mobile"
                                    )}
                                </small>
                            )}
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                Alternate Mobile
                            </label>

                            <input
                                type="tel"
                                inputMode="numeric"
                                value={
                                    customer.alternateMobile
                                }
                                maxLength={
                                    10
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Alternate mobile number"
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "alternateMobile",
                                        normalizeMobile(
                                            event.target
                                                .value
                                        )
                                    )
                                }
                            />
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                Email
                            </label>

                            <input
                                type="email"
                                value={
                                    customer.email
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="customer@example.com"
                                onBlur={() =>
                                    markTouched(
                                        "email"
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "email",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            {getVisibleError(
                                "email"
                            ) && (
                                <small className="billing-customer-field-error">
                                    {getVisibleError(
                                        "email"
                                    )}
                                </small>
                            )}
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                Company Name
                            </label>

                            <input
                                type="text"
                                value={
                                    customer.companyName
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Business or company name"
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "companyName",
                                        event.target
                                            .value
                                    )
                                }
                            />
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                Customer Type
                            </label>

                            <select
                                value={
                                    customer.customerType
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "customerType",
                                        event.target
                                            .value
                                    )
                                }
                            >
                                {CUSTOMER_TYPES.map(
                                    (
                                        option
                                    ) => (
                                        <option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="billing-customer-address-card">
                        <div className="billing-customer-address-title">
                            <FiMapPin />

                            <div>
                                <strong>
                                    Billing Address
                                </strong>

                                <span>
                                    Used for place of supply and invoice printing.
                                </span>
                            </div>
                        </div>

                        <div className="billing-customer-grid billing-customer-grid-three">
                            <div className="billing-customer-field billing-customer-field-span-two">
                                <label>
                                    Address Line 1
                                </label>

                                <input
                                    type="text"
                                    value={
                                        customer
                                            .billingAddress
                                            .addressLine1
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="Shop, building, street"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "addressLine1",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    Address Line 2
                                </label>

                                <input
                                    type="text"
                                    value={
                                        customer
                                            .billingAddress
                                            .addressLine2
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="Area or landmark"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "addressLine2",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    City
                                </label>

                                <input
                                    type="text"
                                    value={
                                        customer
                                            .billingAddress
                                            .city
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="City"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "city",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    State
                                </label>

                                <input
                                    type="text"
                                    value={
                                        customer
                                            .billingAddress
                                            .state
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="State"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "state",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    State Code
                                </label>

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        customer
                                            .billingAddress
                                            .stateCode
                                    }
                                    maxLength={
                                        2
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="08"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "stateCode",
                                            String(
                                                event.target
                                                    .value
                                            )
                                                .replace(
                                                    /\D/g,
                                                    ""
                                                )
                                                .slice(
                                                    0,
                                                    2
                                                )
                                        )
                                    }
                                />
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    Pincode
                                </label>

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        customer
                                            .billingAddress
                                            .pincode
                                    }
                                    maxLength={
                                        6
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="302017"
                                    onBlur={() =>
                                        markTouched(
                                            "billingPincode"
                                        )
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "pincode",
                                            normalizePincode(
                                                event.target
                                                    .value
                                            )
                                        )
                                    }
                                />

                                {getVisibleError(
                                    "billingPincode"
                                ) && (
                                    <small className="billing-customer-field-error">
                                        {getVisibleError(
                                            "billingPincode"
                                        )}
                                    </small>
                                )}
                            </div>

                            <div className="billing-customer-field">
                                <label>
                                    Country
                                </label>

                                <input
                                    type="text"
                                    value={
                                        customer
                                            .billingAddress
                                            .country
                                    }
                                    disabled={
                                        disabled
                                    }
                                    placeholder="India"
                                    onChange={(
                                        event
                                    ) =>
                                        handleAddressChange(
                                            "billingAddress",
                                            "country",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {gstOpen && (
                <div className="billing-customer-panel">
                    <div className="billing-customer-grid billing-customer-grid-three">
                        <div className="billing-customer-field">
                            <label>
                                Tax Treatment
                            </label>

                            <select
                                value={
                                    customer.taxTreatment
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "taxTreatment",
                                        event.target
                                            .value
                                    )
                                }
                            >
                                {TAX_TREATMENTS.map(
                                    (
                                        option
                                    ) => (
                                        <option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                GSTIN
                            </label>

                            <input
                                type="text"
                                value={
                                    customer.gstin
                                }
                                maxLength={
                                    15
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="22AAAAA0000A1Z5"
                                onBlur={() =>
                                    markTouched(
                                        "gstin"
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "gstin",
                                        event.target
                                            .value
                                            .toUpperCase()
                                    )
                                }
                            />

                            {getVisibleError(
                                "gstin"
                            ) && (
                                <small className="billing-customer-field-error">
                                    {getVisibleError(
                                        "gstin"
                                    )}
                                </small>
                            )}
                        </div>

                        <div className="billing-customer-field">
                            <label>
                                PAN
                            </label>

                            <input
                                type="text"
                                value={
                                    customer.pan
                                }
                                maxLength={
                                    10
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="AAAAA0000A"
                                onBlur={() =>
                                    markTouched(
                                        "pan"
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleFieldChange(
                                        "pan",
                                        event.target
                                            .value
                                            .toUpperCase()
                                    )
                                }
                            />

                            {getVisibleError(
                                "pan"
                            ) && (
                                <small className="billing-customer-field-error">
                                    {getVisibleError(
                                        "pan"
                                    )}
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showShippingAddress &&
                shippingOpen && (
                    <div className="billing-customer-panel">
                        <label className="billing-customer-checkbox">
                            <input
                                type="checkbox"
                                checked={
                                    customer.sameAsBillingAddress
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        sameAsBillingAddress:
                                            event.target
                                                .checked,

                                        shippingAddress:
                                            event.target
                                                .checked
                                                ? {
                                                    ...customer.billingAddress,
                                                }
                                                : customer.shippingAddress,
                                    })
                                }
                            />

                            <span>
                                Shipping address is same as billing address
                            </span>
                        </label>

                        {!customer.sameAsBillingAddress && (
                            <div className="billing-customer-address-card">
                                <div className="billing-customer-address-title">
                                    <FiMapPin />

                                    <div>
                                        <strong>
                                            Shipping Address
                                        </strong>

                                        <span>
                                            Used for delivery and dispatch details.
                                        </span>
                                    </div>
                                </div>

                                <div className="billing-customer-grid billing-customer-grid-three">
                                    <div className="billing-customer-field billing-customer-field-span-two">
                                        <label>
                                            Address Line 1
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .addressLine1
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="Shop, building, street"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "addressLine1",
                                                    event.target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            Address Line 2
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .addressLine2
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="Area or landmark"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "addressLine2",
                                                    event.target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            City
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .city
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="City"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "city",
                                                    event.target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            State
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .state
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="State"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "state",
                                                    event.target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            State Code
                                        </label>

                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .stateCode
                                            }
                                            maxLength={
                                                2
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="08"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "stateCode",
                                                    String(
                                                        event.target
                                                            .value
                                                    )
                                                        .replace(
                                                            /\D/g,
                                                            ""
                                                        )
                                                        .slice(
                                                            0,
                                                            2
                                                        )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            Pincode
                                        </label>

                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .pincode
                                            }
                                            maxLength={
                                                6
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="302017"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "pincode",
                                                    normalizePincode(
                                                        event.target
                                                            .value
                                                    )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="billing-customer-field">
                                        <label>
                                            Country
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                customer
                                                    .shippingAddress
                                                    .country
                                            }
                                            disabled={
                                                disabled
                                            }
                                            placeholder="India"
                                            onChange={(
                                                event
                                            ) =>
                                                handleAddressChange(
                                                    "shippingAddress",
                                                    "country",
                                                    event.target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
        </section>
    );
};

export {
    EMPTY_CUSTOMER,
    normalizeCustomerValue,
    validateCustomer,
};

export default React.memo(CustomerSection);
